import React, { ReactNode } from 'react';

import moment, { Duration } from 'moment';

import Shimmer from 'components/Shimmer';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { Travel, VehicleForCustomerSupport } from 'models/customerSupport';
import { TravelType } from 'models/enums/travelType';
import { TravelDetailsVehicle } from 'models/travelDetails';
import { TravelStatus } from 'models/travelStatus';

import Details from './Details';
import DetailsContent from './DetailsContent';
import ExtraCharge from './ExtraCharge';
import Flight, { ShimmerFlight } from './Flight';
import Hotel from './Hotel';
import RequesterInfo from './RequesterInfo';
import {
  Card,
  Row,
  Divider,
  /* Footer, */
  StatusText,
  ClockIcon,
  CodeText,
  Traveler,
  TravelerStatus,
  ToggleDetails,
  DropdownChevron,
  CheckCircleIcon,
  ExclamationCircleIcon,
  XCircleIcon,
  GuestIcon,
  GroupIcon,
  PolicyStatus,
  FlightDetails,
  Destination,
} from './styles';
import Vehicle from './Vehicle';
import useCompanyConfiguration from 'hooks/useCompanyConfiguration';

type Props = {
  travel: Travel;
  children?: ReactNode;
  detailsOpen?: boolean;
  myTripsView?: boolean;
  adminView?: boolean;
  onToggle?(status: boolean);
  render?(travel: Travel, expirationTime: Duration, isExpiring: boolean, isAir: boolean): ReactNode;
};

function getStatusIcon(status: TravelStatus) {
  switch (status) {
    case 'Pending':
      return <ClockIcon />;
    case 'PreApproved':
      return <ClockIcon />;
    case 'Approved':
      return <ClockIcon />;
    case 'Confirmed':
      return <CheckCircleIcon />;
    case 'Paid':
      return <CheckCircleIcon />;
    case 'Reproved':
      return <XCircleIcon />;
    case 'Expired':
      return <XCircleIcon />;
    case 'Cancelled':
      return <XCircleIcon />;
    case 'PaymentFailure':
      return <XCircleIcon />;
    case 'ErrorAtSending':
      return <ExclamationCircleIcon />;

    default:
      return <ClockIcon />;
  }
}

export const ShimmerTripCard = () => (
  <Card>
    <Row>
      <CodeText>
        <Shimmer>Rappi code: 000000</Shimmer>
      </CodeText>
      <StatusText $status="None">
        <Shimmer>Pending</Shimmer>
      </StatusText>
    </Row>
    <Row>
      <ShimmerFlight />
    </Row>
    <Row>
      <Traveler>
        <Shimmer>Name of traveler</Shimmer>
      </Traveler>
      <TravelerStatus>
        <Shimmer>Invited</Shimmer>
      </TravelerStatus>
    </Row>
  </Card>
);

const mapVehicleTravelToCustomerSupport = (details: TravelDetailsVehicle): VehicleForCustomerSupport => ({
  vehicleDropoffCityName: details.vehicleDropoffCityName,
  vehiclePickUpCityName: details.vehiclePickUpCityName,
  vehicleCityCode: details.vehicleCityCode || details.cityCode,
  pickupDateTime: details.pickupDateTime,
  droppOffDateTime: details.droppOffDateTime,
  quantityDays: details.quantityDays,
  vehicleModel: details.vehicleModel,
});

const TripSectorDetail = ({ travel }: { travel: Travel }) => {
  const { t } = useStoreTranslation();

  if (travel.type === TravelType.Hotel && travel.hotel) return <Hotel hotelDetails={travel.hotel} />;

  if (travel.vehicle) return <Vehicle details={mapVehicleTravelToCustomerSupport(travel.vehicle)} />;

  if (travel.type === TravelType.Flight) {
    const isMultidestination = travel.flightSectors && travel.flightSectors?.length > 2;
    return !isMultidestination ? (
      <>
        {travel.flightSectors?.map((flightSector, i: number) => (
          <Flight key={flightSector.sectorNumber} flightSector={flightSector} direction={i % 2 === 0 ? 'to' : 'back'} />
        ))}
      </>
    ) : (
      <FlightDetails>
        <div>
          <Destination>{t('requestCard.multiDestination')}</Destination>

          {travel.flightSectors?.map((flightSector, i: number) => (
            <Flight
              key={flightSector.sectorNumber}
              flightSector={flightSector}
              direction={i % 2 === 0 ? 'to' : 'back'}
            />
          ))}
        </div>
      </FlightDetails>
    );
  }

  if (travel.type === TravelType.ExtraCharge) return <ExtraCharge reason={travel.reason || ''} />;

  return null;
};

const TripCard = ({ travel, myTripsView, adminView, children, onToggle, render, detailsOpen = false }: Props) => {
  const { t } = useStoreTranslation();
  const { requester } = travel;

  const isAir = travel.type !== TravelType.ExtraCharge && !!travel.air;
  const expirationDate = (travel.air || travel.hotel || travel.vehicle)?.expirationDate;
  const expirationTime = moment.duration(moment(expirationDate).diff(moment()));
  const isExpiring =
    String(requester.status) === 'Pending' && expirationTime.asHours() < 24 && expirationTime.asHours() > 0;
  const outOfPolicyMessages = (travel.air || travel.hotel || travel.vehicle)?.breakRulesMessage;
  const outOfPolicy = outOfPolicyMessages && outOfPolicyMessages.length > 0;
  const rappiCode = (travel.air || travel.hotel || travel.vehicle)?.orderNumber;
  const companyConfiguration = useCompanyConfiguration();
  return (
    <Card>
      <Row>
        <CodeText>
          {t('tripCard.rappiCode', { companyName: companyConfiguration?.companyName })}: {rappiCode}
        </CodeText>
        <StatusText $status={isExpiring ? 'Expiring' : requester.status}>
          {getStatusIcon(requester.status)}
          {isExpiring
            ? t('tripCard.expireIn', { expirationTime: expirationTime.humanize() })
            : t(`tripCard.status.${requester.status}`)}
        </StatusText>
      </Row>
      {myTripsView ? (
        <RequesterInfo requester={requester} price={travel.prices} adminView={adminView} />
      ) : (
        <>
          <Row>
            <TripSectorDetail travel={travel} />
          </Row>
          <Row>
            <Traveler>{requester.fullName}</Traveler>
            <TravelerStatus>
              {requester.isGuest ? (
                <>
                  <GuestIcon color="currentColor" height={16} />
                  {t('tripCard.invited')}
                </>
              ) : (
                <>
                  <GroupIcon color="currentColor" height={16} />
                  {requester.sector}
                </>
              )}
            </TravelerStatus>
          </Row>
        </>
      )}
      <Divider />
      <Row>
        {!(myTripsView && detailsOpen) && (
          <PolicyStatus $valid={!outOfPolicy}>
            {outOfPolicy ? t('tripCard.policyStatus.invalid') : t('tripCard.policyStatus.valid')}
          </PolicyStatus>
        )}
        <ToggleDetails
          onClick={() => {
            onToggle && onToggle(!detailsOpen);
          }}
          $detailsOpen={detailsOpen}
          disabled={!travel}
        >
          {detailsOpen ? t('tripCard.lessDetails') : t('tripCard.moreDetails')}
          <DropdownChevron />
        </ToggleDetails>
      </Row>
      <Details open={detailsOpen}>
        <DetailsContent myTripsView={myTripsView} travelDetails={travel} />
      </Details>
      {(render || children) && <Divider />}
      {/* <Footer>{render ? render(travel, expirationTime, isExpiring, isAir) : children}</Footer> */}
    </Card>
  );
};

export default TripCard;
